import { observer } from "mobx-react";
import React from "react";
import { generatePath, RouteComponentProps, withRouter } from "react-router";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";

import Page from "components/Layout/Page";
import SortingControl from "components/SortingControl";
import FilteringControl from "components/FilteringControl";
import Placeholder from "components/Placeholder";
import NavBar from "../NavBar";
import SectionCard from "./SectionCard";
import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  OverflowLoader,
  ScrollLoader,
  SectionCardWrapper,
} from "./styles";

import ROUTES from "navigation/routes";
import { computed } from "mobx";
import { TableSortType } from "stores/templates/TableStore";
import InfiniteScroll from "react-infinite-scroll-component";
import { SNACKBAR_TYPES } from "components/Snackbar";
import QuizSectionStore from "stores/QuizSectionStore";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Section extends React.Component<Props> {
  @computed get sections() {
    return QuizSectionStore.items;
  }

  @computed get sectionListIsEmpty() {
    return this.sections.length === 0;
  }

  componentDidMount() {
    QuizSectionStore.fetch();
  }

  componentWillUnmount() {
    QuizSectionStore.resetPage();
  }

  fetchMoreSections = async () => {
    const { setMessage } = this.props;

    try {
      await QuizSectionStore.fetchMoreSections();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    QuizSectionStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && QuizSectionStore.fetch();
  };

  onSearchClear = () => {
    QuizSectionStore.clearSearch();
    QuizSectionStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    console.log(value);
    QuizSectionStore.applyOrder(value);
    QuizSectionStore.resetPage();
    QuizSectionStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      QuizSectionStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    QuizSectionStore.resetPage();
    QuizSectionStore.fetch();
  };

  onFilterClear = () => {
    QuizSectionStore.clearFilters();
    QuizSectionStore.fetch();
  };

  onAddSection = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_QUESTIONNAIRE_SECTION_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (section: any) => () => {
    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_SECTION_DETAILS, {
      sectionId: section.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (section: any) => () => {
    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_SECTION_DETAILS, {
      sectionId: section.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onArchive = (section: any) => () => {
    console.log("here start");
  };

  onCopy = (section: any) => () => {
    console.log("here start");
  };

  onPublish = (section: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = section;

    try {
      await QuizSectionStore.changeSectionStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Section"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddSection}
              >
                Add Section
              </Button>
            </PageControl>
          </>
        }
        isLoading={QuizSectionStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={QuizSectionStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={QuizSectionStore.orderContext.appliedOrderInfo}
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={QuizSectionStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(QuizSectionStore.isSectionPublishing ||
          QuizSectionStore.isSectionCopying) && <OverflowLoader />}
        {!this.sectionListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.sections.length}
            hasMore={QuizSectionStore.hasMoreInfo}
            next={this.fetchMoreSections}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.sections.map(section => (
              <SectionCardWrapper key={section.id}>
                <SectionCard
                  title={section.title}
                  publishStatus={section.publishStatus}
                  onDetailsClick={this.onDetailsClick(section)}
                  onArchive={this.onArchive(section)}
                  onEdit={this.onEdit(section)}
                  onCopy={this.onCopy(section)}
                  onPublish={this.onPublish(section)}
                />
              </SectionCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              QuizSectionStore.search
                ? "No matches found. Please try another search."
                : "No sections added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Section)));
